import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Mail, ShieldCheck } from 'lucide-react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { getAuthActionCodeSettings } from '@/lib/authActionSettings';
import { useAuthStore } from '@/stores/authStore';
import { useToastStore } from '@/stores/toastStore';
import { Button, Card, CardContent, Input } from '@/components/ui';

function getAccountErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    switch (code) {
      case 'auth/invalid-email':
        return 'メールアドレスの形式が正しくありません';
      case 'auth/email-already-in-use':
        return 'このメールアドレスは既に使用されています';
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return '現在のパスワードが正しくありません';
      case 'auth/requires-recent-login':
        return '再認証が必要です。再ログインしてからお試しください';
      case 'auth/weak-password':
        return '新しいパスワードは6文字以上で入力してください';
      default:
        return '処理に失敗しました。時間をおいて再度お試しください';
    }
  }

  return '処理に失敗しました。時間をおいて再度お試しください';
}

export function AccountSettingsPage() {
  const user = useAuthStore((state) => state.user);
  const userData = useAuthStore((state) => state.userData);
  const addToast = useToastStore((state) => state.addToast);

  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const currentEmail = userData?.email || user?.email || '';

  const handleUpdateEmail = async () => {
    if (!user?.uid || !currentEmail) return;

    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) {
      addToast('warning', '新しいメールアドレスを入力してください');
      return;
    }
    if (trimmedEmail === currentEmail) {
      addToast('warning', '現在と同じメールアドレスです');
      return;
    }
    if (!emailPassword) {
      addToast('warning', '現在のパスワードを入力してください');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      addToast('error', 'ユーザー情報の取得に失敗しました');
      return;
    }

    try {
      setIsUpdatingEmail(true);
      const credential = EmailAuthProvider.credential(currentUser.email, emailPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await verifyBeforeUpdateEmail(
        currentUser,
        trimmedEmail,
        getAuthActionCodeSettings('verifyAndChangeEmail')
      );
      setNewEmail('');
      setEmailPassword('');
      addToast('success', '新しいメールアドレス宛に確認メールを送信しました');
    } catch (error) {
      addToast('error', getAccountErrorMessage(error));
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!user?.uid) return;
    if (!currentPassword) {
      addToast('warning', '現在のパスワードを入力してください');
      return;
    }
    if (!newPassword || !confirmPassword) {
      addToast('warning', '新しいパスワードを入力してください');
      return;
    }
    if (newPassword.length < 6) {
      addToast('warning', '新しいパスワードは6文字以上で入力してください');
      return;
    }
    if (newPassword !== confirmPassword) {
      addToast('warning', 'パスワード確認が一致しません');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.email) {
      addToast('error', 'ユーザー情報の取得に失敗しました');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      addToast('success', 'パスワードを変更しました');
    } catch (error) {
      addToast('error', getAccountErrorMessage(error));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="min-h-full bg-[#F7F4EF]">
      <div className="md:mx-auto md:max-w-3xl md:px-6 md:py-6">
        <div className="px-4 pb-12 pt-14 md:px-0 md:pt-0">
          <Link
            to="/mypage"
            className="mb-5 inline-flex items-center gap-2 text-[13px] text-[#7E7668] transition hover:text-copper"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>マイページに戻る</span>
          </Link>

          <div className="mb-6 flex items-center justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#B7B0A4]">Account</p>
              <h1 className="mt-1 text-[22px] font-semibold text-[#1A1A1A]">アカウント設定</h1>
              <p className="mt-1 text-sm text-[#8D877D]">メールアドレスとパスワードを変更できます。</p>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F5EDE2]">
              <ShieldCheck className="h-5 w-5 text-copper" />
            </div>
          </div>

          <Card className="mb-4 overflow-hidden border-[#E3D7C7] bg-[#FBF5EC]">
            <CardContent className="flex gap-3 px-5 py-4">
              <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-copper" />
              <p className="text-sm leading-relaxed text-[#6F6658]">
                セキュリティ上の理由で、メールアドレス・パスワード変更時には現在のパスワード確認が必要です。
              </p>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="overflow-hidden border-[#ECE6DD]">
              <CardContent className="py-5">
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#F5F2ED]">
                    <Mail className="h-4 w-4 text-copper" />
                  </div>
                  <h2 className="text-[16px] font-semibold text-[#1A1A1A]">メールアドレス変更</h2>
                </div>

                <div className="space-y-4">
                  <Input
                    label="現在のメールアドレス"
                    value={currentEmail}
                    disabled
                  />
                  <Input
                    label="新しいメールアドレス"
                    type="email"
                    placeholder="new@example.com"
                    value={newEmail}
                    onChange={(event) => setNewEmail(event.target.value)}
                  />
                  <Input
                    label="現在のパスワード（確認用）"
                    type="password"
                    value={emailPassword}
                    onChange={(event) => setEmailPassword(event.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={handleUpdateEmail}
                      isLoading={isUpdatingEmail}
                      disabled={isUpdatingPassword}
                      className="!rounded-full"
                    >
                      確認メールを送信
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden border-[#ECE6DD]">
              <CardContent className="py-5">
                <div className="mb-4 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#F5F2ED]">
                    <Lock className="h-4 w-4 text-copper" />
                  </div>
                  <h2 className="text-[16px] font-semibold text-[#1A1A1A]">パスワード変更</h2>
                </div>

                <div className="space-y-4">
                  <Input
                    label="現在のパスワード"
                    type="password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                  />
                  <Input
                    label="新しいパスワード"
                    type="password"
                    hint="6文字以上で入力してください"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                  />
                  <Input
                    label="新しいパスワード（確認）"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={handleUpdatePassword}
                      isLoading={isUpdatingPassword}
                      disabled={isUpdatingEmail}
                      className="!rounded-full"
                    >
                      パスワードを更新
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
